import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Globe, ExternalLink, RefreshCw, Loader2, Sparkles, Trash2,
  Search, Plus, FileText, Newspaper, BookOpen, Send, MessageSquare,
  Star, CheckCircle2, Image, ChevronRight, Calendar, Link2,
  ShieldCheck, ShieldX, ArrowUpRight, Shield, Tags, History,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { api } from '../../context/AuthContext';
import { useAuth } from '../../context/AuthContext';
import { useProject } from '../../context/ProjectContext';
import { useTaskState } from '../../context/TaskStateContext';
import { toast } from 'sonner';

// ── Constants ─────────────────────────────────────────────────────────────────

const TYPE_CONFIG = {
  blog:       { label: 'Blog',        color: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400', icon: BookOpen },
  article:    { label: 'Article',     color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',         icon: Newspaper },
  press:      { label: 'Press',       color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',     icon: FileText },
  guest_post: { label: 'Guest Post',  color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400', icon: Send },
  mention:    { label: 'Mention',     color: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400',             icon: MessageSquare },
  review:     { label: 'Review',      color: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',         icon: Star },
};

const FILTER_TABS = [
  { id: '', label: 'All' },
  { id: 'blog', label: 'Blog' },
  { id: 'article', label: 'Article' },
  { id: 'press', label: 'Press' },
  { id: 'guest_post', label: 'Guest Post' },
  { id: 'mention', label: 'Mention' },
  { id: 'review', label: 'Review' },
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.3, delay },
});

const domainFromUrl = (value) => {
  try {
    return new URL(value).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
};

// ── Small helpers ─────────────────────────────────────────────────────────────

function TypeBadge({ type }) {
  const cfg = TYPE_CONFIG[type] || TYPE_CONFIG.article;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
      {type}
    </span>
  );
}

function BacklinkBadge({ hasBacklink, linkType }) {
  if (hasBacklink) {
    const isDofollow = linkType === 'dofollow';
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
        isDofollow
          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
          : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
      }`}>
        <ShieldCheck className="w-3 h-3" />
        {isDofollow ? 'Dofollow' : 'Nofollow'}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
      <ShieldX className="w-3 h-3" />
      Unverified
    </span>
  );
}

// ── ArticleListItem ───────────────────────────────────────────────────────────

function ArticleListItem({ article, active, onSelect, onDelete }) {
  const dateStr = article.published_date
    ? new Date(article.published_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })
    : article.discovered_at
    ? new Date(article.discovered_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })
    : null;

  const primaryLinkType = article.primary_backlink?.link_type;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(article)}
      onKeyDown={(e) => e.key === 'Enter' && onSelect(article)}
      className={`group relative p-3 rounded-xl border cursor-pointer transition-all outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
        active
          ? 'bg-indigo-50 dark:bg-indigo-900/30 border-indigo-200 dark:border-indigo-700'
          : 'bg-slate-50 dark:bg-slate-800/50 border-transparent hover:bg-slate-100 dark:hover:bg-slate-800'
      }`}
    >
      {/* Status strip */}
      {article.status === 'inactive' ? (
        <div className="absolute left-0 top-2 bottom-2 w-1 rounded-r bg-red-400" />
      ) : article.has_backlink ? (
        <div className={`absolute left-0 top-2 bottom-2 w-1 rounded-r ${
          primaryLinkType === 'dofollow' ? 'bg-emerald-500' : 'bg-amber-400'
        }`} />
      ) : null}

      <div className="flex items-start justify-between gap-2 pl-1">
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium line-clamp-2 leading-snug ${
            article.status === 'inactive'
              ? 'text-slate-400 dark:text-slate-500 line-through'
              : 'text-slate-800 dark:text-slate-100'
          }`}>
            {article.primary_backlink?.anchor_text || article.title || article.url}
          </p>
          {article.primary_backlink?.anchor_text && article.title && (
            <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5 truncate italic">{article.title}</p>
          )}
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 truncate">{article.domain}</p>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(article); }}
          className="opacity-0 group-hover:opacity-100 shrink-0 p-1 rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
          title="Delete"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="flex items-center gap-1.5 mt-2 flex-wrap pl-1">
        {article.status === 'inactive' ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400">
            Page Gone (404)
          </span>
        ) : (
          <>
            <TypeBadge type={article.article_type} />
            {article.has_backlink !== undefined && (
              <BacklinkBadge hasBacklink={article.has_backlink} linkType={primaryLinkType} />
            )}
          </>
        )}
        {dateStr && (
          <span className="flex items-center gap-1 text-xs text-slate-400">
            <Calendar className="w-3 h-3" />
            {dateStr}
          </span>
        )}
      </div>
    </div>
  );
}

// ── BacklinksPanel ────────────────────────────────────────────────────────────

function BacklinksPanel({ article }) {
  const backlinks = article.backlinks || [];
  const primary = article.primary_backlink;

  if (!article.has_backlink) {
    return (
      <div className="flex items-center gap-2 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 text-sm text-slate-500">
        <ShieldX className="w-4 h-4 text-slate-400 shrink-0" />
        <span>No verified backlink to your website was detected on this page.</span>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
        <Link2 className="w-4 h-4 text-emerald-500" />
        Verified Backlinks
        <span className="ml-1 px-1.5 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-xs font-bold">
          {backlinks.length}
        </span>
      </h3>
      <div className="space-y-2">
        {backlinks.map((bl, idx) => (
          <div
            key={idx}
            className={`rounded-lg border p-3 ${
              bl.link_type === 'dofollow'
                ? 'border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-900/10'
                : 'border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10'
            }`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <a href={bl.href} target="_blank" rel="noopener noreferrer"
                   className="text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:underline line-clamp-1">
                  {bl.anchor_text || bl.href}
                </a>
                {bl.anchor_text && (
                  <p className="text-xs text-slate-500 truncate mt-0.5">{bl.href}</p>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                  bl.link_type === 'dofollow'
                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400'
                    : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400'
                }`}>
                  {bl.link_type}
                </span>
                <a href={bl.href} target="_blank" rel="noopener noreferrer" title="Open link">
                  <ArrowUpRight className="w-3.5 h-3.5 text-slate-400 hover:text-indigo-500" />
                </a>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── ArticleDetailPanel ────────────────────────────────────────────────────────

function ArticleDetailPanel({ article, onRefresh, refreshing, targetDomain }) {
  const [imgError, setImgError] = useState(false);
  const screenshotUrl = article.screenshot_path
    ? `${BACKEND_URL}${article.screenshot_path}`
    : null;

  const dateStr = article.published_date
    ? new Date(article.published_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : null;

  const primaryLinkType = article.primary_backlink?.link_type;

  return (
    <motion.div key={article.article_id} {...fadeUp()} className="space-y-5">
      {/* Title row */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 leading-tight">
            {article.title || article.url}
          </h2>
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            <span className="text-sm text-slate-500">{article.domain}</span>
            <TypeBadge type={article.article_type} />
            <BacklinkBadge hasBacklink={article.has_backlink} linkType={primaryLinkType} />
            {dateStr && (
              <span className="flex items-center gap-1 text-xs text-slate-400">
                <Calendar className="w-3 h-3" />
                {dateStr}
              </span>
            )}
            {article.metadata?.author && (
              <span className="text-xs text-slate-400">by {article.metadata.author}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            size="sm"
            variant="outline"
            onClick={() => onRefresh(article.article_id, targetDomain)}
            disabled={refreshing}
            className="gap-1.5"
          >
            {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Re-check
          </Button>
          <a href={article.url} target="_blank" rel="noopener noreferrer">
            <Button size="sm" className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white">
              <ExternalLink className="w-3.5 h-3.5" />
              View on Site
            </Button>
          </a>
        </div>
      </div>

      {/* Screenshot */}
      <div className="rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800">
        {screenshotUrl && !imgError ? (
          <img
            src={screenshotUrl}
            alt={`Screenshot of ${article.domain}`}
            className="w-full object-cover"
            style={{ maxHeight: '360px', objectPosition: 'top' }}
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="flex flex-col items-center justify-center h-48 text-slate-400 gap-3">
            <div className="flex items-center gap-3">
              <img
                src={`https://www.google.com/s2/favicons?domain=${article.domain}&sz=64`}
                alt=""
                className="w-10 h-10 rounded-lg opacity-60"
                onError={e => { e.target.style.display = 'none'; }}
              />
              <div className="text-left">
                <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
                  {article.domain}
                </p>
                <p className="text-xs text-slate-400 dark:text-slate-500 flex items-center gap-1 mt-0.5">
                  <Shield className="w-3 h-3" />
                  Screenshot unavailable (site may be protected by Cloudflare)
                </p>
              </div>
            </div>
            <a
              href={article.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-indigo-500 hover:text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1"
            >
              Visit site directly
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        )}
      </div>

      {/* Backlinks panel — primary feature */}
      <BacklinksPanel article={article} />

      {/* Metadata stats */}
      {article.metadata && (
        <div className="grid grid-cols-3 gap-3">
          {article.metadata.word_count > 0 && (
            <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3 text-center">
              <p className="text-lg font-bold text-slate-800 dark:text-slate-100">
                {article.metadata.word_count.toLocaleString()}
              </p>
              <p className="text-xs text-slate-500">Words</p>
            </div>
          )}
          {article.backlink_count > 0 && (
            <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-3 text-center">
              <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                {article.backlink_count}
              </p>
              <p className="text-xs text-slate-500">Backlinks</p>
            </div>
          )}
          <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3 text-center">
            <p className="text-xs font-medium text-slate-600 dark:text-slate-300 uppercase tracking-wider">
              {article.discovery_method === 'ai_discovery' ? 'AI Found' : 'Manual'}
            </p>
            <p className="text-xs text-slate-500">Source</p>
          </div>
        </div>
      )}

      {/* Content excerpt */}
      {article.content_excerpt && (
        <div>
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">Content Preview</h3>
          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4 text-sm text-slate-700 dark:text-slate-300 leading-relaxed border border-slate-200 dark:border-slate-700">
            {article.content_excerpt}
          </div>
        </div>
      )}

      {/* View on site CTA */}
      <a href={article.url} target="_blank" rel="noopener noreferrer" className="block">
        <div className="flex items-center justify-between p-4 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-indigo-300 dark:hover:border-indigo-600 hover:bg-indigo-50/30 dark:hover:bg-indigo-900/10 transition-all group">
          <div>
            <p className="text-sm font-medium text-slate-800 dark:text-slate-100 group-hover:text-indigo-700 dark:group-hover:text-indigo-400">
              View original article
            </p>
            <p className="text-xs text-slate-500 truncate max-w-sm">{article.url}</p>
          </div>
          <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-indigo-500 shrink-0" />
        </div>
      </a>
    </motion.div>
  );
}

// ── DiscoverDialog ────────────────────────────────────────────────────────────

function DiscoverDialog({ open, onClose, onSuccess, savedWebsiteUrl, targetSource }) {
  const { selectedProjectId } = useProject();
  const discoverTask = useTaskState('offpage-discover');
  const discovering = discoverTask.running;
  const [websiteUrl, setWebsiteUrl] = useState(savedWebsiteUrl || '');
  const [industry, setIndustry] = useState('');
  const [keywords, setKeywords] = useState('');
  const [maxResults, setMaxResults] = useState(20);
  const [result, setResult] = useState(null);

  // Sync website URL from parent when dialog opens
  useEffect(() => {
    if (open && savedWebsiteUrl) setWebsiteUrl(savedWebsiteUrl);
  }, [open, savedWebsiteUrl]);

  const handleDiscover = async () => {
    if (!websiteUrl.trim()) return toast.error('Enter your website URL');
    discoverTask.start();
    setResult(null);
    try {
      const res = await api.post('/offpage-seo/discover', {
        website_url: websiteUrl.trim(),
        industry: industry.trim() || null,
        keywords: keywords.trim() ? keywords.split(',').map(k => k.trim()).filter(Boolean) : [],
        max_results: maxResults,
        project_id: selectedProjectId || undefined,
      });
      setResult({ ...res.data, website_url: websiteUrl.trim() });
      toast.success(`Discovery complete: ${res.data.saved} articles found, ${res.data.backlinks_verified ?? 0} with verified backlinks`);
      onSuccess(websiteUrl.trim());
      discoverTask.complete(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Discovery failed');
      discoverTask.fail(err?.message || 'Discovery failed');
    }
  };

  const handleClose = () => {
    setResult(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-indigo-500" />
            Discover Backlinks
          </DialogTitle>
        </DialogHeader>

        {!result ? (
          <div className="space-y-4 mt-2">
            <p className="text-sm text-slate-500">
              AI will search the web for third-party pages that contain an actual backlink
              (hyperlink) pointing to your website.
            </p>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1 block">
                  Your Website URL *
                </label>
                {targetSource === 'gsc' && savedWebsiteUrl && (
                  <p className="text-xs text-emerald-600 dark:text-emerald-400 mb-1">
                    Using your selected Google Search Console property.
                  </p>
                )}
                <Input
                  placeholder="https://yourwebsite.com"
                  value={websiteUrl}
                  onChange={e => setWebsiteUrl(e.target.value)}
                  disabled={discovering}
                />
                <p className="text-xs text-slate-400 mt-1">
                  We'll search for pages on OTHER websites that link to this domain.
                </p>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1 block">Industry (optional)</label>
                <Input
                  placeholder="e.g. SaaS, e-commerce, healthcare"
                  value={industry}
                  onChange={e => setIndustry(e.target.value)}
                  disabled={discovering}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1 block">
                  Brand / Keywords (comma-separated, optional)
                </label>
                <Input
                  placeholder="e.g. brand name, product name"
                  value={keywords}
                  onChange={e => setKeywords(e.target.value)}
                  disabled={discovering}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1 block">
                  Max pages to check: {maxResults}
                </label>
                <input
                  type="range" min={5} max={50} step={5}
                  value={maxResults}
                  onChange={e => setMaxResults(Number(e.target.value))}
                  disabled={discovering}
                  className="w-full accent-indigo-500"
                />
              </div>
            </div>
            <Button
              onClick={handleDiscover}
              disabled={discovering || !websiteUrl.trim()}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white gap-2"
            >
              {discovering ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Searching for backlinks...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  Find Backlinks
                </>
              )}
            </Button>
            {discovering && (
              <div className="space-y-2 text-xs text-slate-500">
                <p className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                  Using search operators to find linking pages...
                </p>
                <p className="flex items-center gap-2 animate-pulse">
                  <Loader2 className="w-3.5 h-3.5 text-indigo-400 animate-spin" />
                  Scraping pages to verify backlinks &amp; capture screenshots...
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4 mt-2">
            <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-4">
              <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-300 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                Discovery Complete
              </p>
              <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="text-xl font-bold text-slate-700 dark:text-slate-300">{result.discovered}</p>
                  <p className="text-xs text-slate-500">Pages checked</p>
                </div>
                <div>
                  <p className="text-xl font-bold text-indigo-700 dark:text-indigo-400">{result.saved}</p>
                  <p className="text-xs text-slate-500">Saved</p>
                </div>
                <div>
                  <p className="text-xl font-bold text-emerald-700 dark:text-emerald-400">{result.backlinks_verified ?? 0}</p>
                  <p className="text-xs text-slate-500">Backlinks verified</p>
                </div>
              </div>
            </div>
            {(result.backlinks_verified ?? 0) === 0 && result.saved > 0 && (
              <p className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded-lg p-2">
                No verified backlinks found on these pages yet. They may still be valuable mentions.
                Use "Re-check" on individual articles after reviewing them.
              </p>
            )}
            <Button onClick={handleClose} className="w-full">
              View Articles
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── AddArticleDialog ──────────────────────────────────────────────────────────

function isRootDomainOnly(rawUrl) {
  try {
    const parsed = new URL(rawUrl.trim());
    const path = parsed.pathname.replace(/\/+$/, '');
    return path === '' || path === '/';
  } catch {
    return false;
  }
}

function AddArticleDialog({ open, onClose, onSuccess, targetDomain }) {
  const { selectedProjectId } = useProject();
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [articleType, setArticleType] = useState('article');
  const [loading, setLoading] = useState(false);

  const handleAdd = async () => {
    if (!url.trim()) return toast.error('URL is required');
    if (isRootDomainOnly(url.trim())) {
      return toast.error(
        'Root domain URLs are not accepted. Please enter the specific page or article URL that contains the backlink (e.g. https://example.com/blog/your-keyword-article).'
      );
    }
    setLoading(true);
    try {
      const res = await api.post('/offpage-seo/articles', {
        url: url.trim(),
        title: title.trim() || null,
        article_type: articleType,
        target_domain: targetDomain || null,
        project_id: selectedProjectId || undefined,
      });
      const hasBacklink = res.data?.article?.has_backlink;
      if (hasBacklink) {
        toast.success('Article added — backlink verified!');
      } else {
        toast.success('Article added. No backlink detected on this page.');
      }
      setUrl('');
      setTitle('');
      setArticleType('article');
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add article');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5 text-indigo-500" />
            Add Article Manually
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          {targetDomain && (
            <div className="flex items-center gap-2 p-2 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 text-xs text-indigo-700 dark:text-indigo-400">
              <Link2 className="w-3.5 h-3.5 shrink-0" />
              Backlink will be verified for: <strong>{targetDomain}</strong>
            </div>
          )}
          <div>
            <label className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1 block">Article URL *</label>
            <Input
              placeholder="https://example.com/blog/your-keyword-article"
              value={url}
              onChange={e => setUrl(e.target.value)}
              className={url.trim() && isRootDomainOnly(url.trim()) ? 'border-red-400 focus-visible:ring-red-400' : ''}
            />
            {url.trim() && isRootDomainOnly(url.trim()) ? (
              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                <ShieldX className="w-3 h-3 shrink-0" />
                Root domain URLs are not accepted. Add a specific page path with keywords.
              </p>
            ) : (
              <p className="text-xs text-slate-400 mt-1">
                Must be a specific page URL — not just the root domain (e.g. /blog/keyword-article).
              </p>
            )}
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1 block">Title (optional — auto-detected)</label>
            <Input placeholder="Article title" value={title} onChange={e => setTitle(e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1 block">Type</label>
            <select
              value={articleType}
              onChange={e => setArticleType(e.target.value)}
              className="w-full h-9 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {Object.entries(TYPE_CONFIG).map(([key, cfg]) => (
                <option key={key} value={key}>{cfg.label}</option>
              ))}
            </select>
          </div>
          <Button
            onClick={handleAdd}
            disabled={loading || !url.trim() || isRootDomainOnly(url.trim())}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white gap-2"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Add & Verify Backlink
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function OffPageSEODashboard() {
  const { selectedProjectId } = useProject();
  const [articles, setArticles] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [activeType, setActiveType] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [refreshingId, setRefreshingId] = useState(null);
  const [showDiscover, setShowDiscover] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [reclassifying, setReclassifying] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  // Remember the website URL used for discovery so we can pass it to verify
  const [targetDomain, setTargetDomain] = useState('');
  const [targetWebsiteUrl, setTargetWebsiteUrl] = useState('');
  const [targetSource, setTargetSource] = useState('');
  const [discoveryRunsOpen, setDiscoveryRunsOpen] = useState(false);
  const [discoveryRuns, setDiscoveryRuns] = useState([]);
  const [discoveryRunsLoading, setDiscoveryRunsLoading] = useState(false);

  const PAGE_SIZE = 50;

  const fetchArticles = useCallback(async (resetPage = true) => {
    setLoading(true);
    try {
      const currentPage = resetPage ? 1 : page;
      const params = new URLSearchParams({ page: currentPage, page_size: PAGE_SIZE });
      if (activeType) params.set('article_type', activeType);
      if (search.trim()) params.set('search', search.trim());
      if (selectedProjectId) params.set('project_id', selectedProjectId);

      const statsParams = selectedProjectId ? `?project_id=${encodeURIComponent(selectedProjectId)}` : '';
      const [articlesRes, statsRes, targetRes] = await Promise.all([
        api.get(`/offpage-seo/articles?${params}`),
        api.get(`/offpage-seo/stats${statsParams}`),
        api.get('/backlinks/target').catch(() => ({ data: null })),
      ]);

      const items = articlesRes.data.items || [];
      if (resetPage) {
        setArticles(items);
        setPage(1);
      } else {
        setArticles(prev => [...prev, ...items]);
      }
      setTotal(articlesRes.data.total || 0);
      setHasMore(articlesRes.data.has_more || false);
      setStats(statsRes.data);
      const selectedWebsite = targetRes.data?.selected_website || '';
      if (selectedWebsite) {
        setTargetWebsiteUrl(selectedWebsite);
        setTargetDomain(domainFromUrl(selectedWebsite));
        setTargetSource(targetRes.data?.selected_source || '');
      }

      if (resetPage && items.length > 0 && !selected) {
        setSelected(items[0]);
      }
    } catch {
      toast.error('Failed to load articles');
    } finally {
      setLoading(false);
    }
  }, [activeType, search, page, selected, selectedProjectId]);

  useEffect(() => {
    fetchArticles(true);
  }, [activeType, selectedProjectId]);

  const handleRefresh = async (articleId, domain) => {
    setRefreshingId(articleId);
    try {
      const res = await api.post(`/offpage-seo/articles/${articleId}/refresh`, {
        target_domain: domain || targetDomain || null,
      });
      const updated = res.data.article;
      setArticles(prev => prev.map(a => a.article_id === articleId ? updated : a));
      if (selected?.article_id === articleId) setSelected(updated);
      const msg = updated.has_backlink
        ? `Backlink verified — ${updated.backlink_count} link(s) found`
        : 'Refreshed — no backlink detected on this page';
      toast.success(msg);
    } catch {
      toast.error('Failed to refresh article');
    } finally {
      setRefreshingId(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/offpage-seo/articles/${deleteTarget.article_id}`);
      setArticles(prev => prev.filter(a => a.article_id !== deleteTarget.article_id));
      if (selected?.article_id === deleteTarget.article_id) {
        const remaining = articles.filter(a => a.article_id !== deleteTarget.article_id);
        setSelected(remaining[0] || null);
      }
      setTotal(prev => prev - 1);
      toast.success('Article deleted');
    } catch {
      toast.error('Failed to delete article');
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleDiscoverSuccess = (websiteUrl) => {
    if (websiteUrl) {
      setTargetWebsiteUrl(websiteUrl);
      setTargetDomain(domainFromUrl(websiteUrl));
    }
    fetchArticles(true);
  };

  const handleReclassify = async () => {
    setReclassifying(true);
    try {
      const res = await api.post(
        `/offpage-seo/reclassify${selectedProjectId ? `?project_id=${encodeURIComponent(selectedProjectId)}` : ''}`
      );
      toast.success(
        `Re-classified ${res.data.updated} of ${res.data.total} articles based on source domain.`
      );
      fetchArticles(true);
    } catch {
      toast.error('Re-classification failed');
    } finally {
      setReclassifying(false);
    }
  };

  const handleViewDiscoveryHistory = async () => {
    setDiscoveryRunsOpen(true);
    setDiscoveryRunsLoading(true);
    try {
      const res = await api.get('/offpage-seo/discovery-runs', { params: { page: 1, page_size: 20 } });
      setDiscoveryRuns(res.data.items || []);
    } catch (err) {
      toast.error('Failed to load discovery history');
    } finally {
      setDiscoveryRunsLoading(false);
    }
  };

  const handleClearAll = async () => {
    setClearing(true);
    try {
      const res = await api.delete(
        `/offpage-seo/articles${selectedProjectId ? `?project_id=${encodeURIComponent(selectedProjectId)}` : ''}`
      );
      toast.success(`Cleared ${res.data.deleted_count} articles. Run discovery to find fresh results.`);
      setArticles([]);
      setStats(null);
      setSelected(null);
      setTotal(0);
      setShowClearConfirm(false);
    } catch {
      toast.error('Failed to clear articles');
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <motion.div {...fadeUp()} className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Off-Page SEO</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Track third-party pages that contain backlinks to your website
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleReclassify}
            disabled={reclassifying}
            className="gap-1.5"
            title="Re-classify all article types based on source domain"
          >
            {reclassifying
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <Tags className="w-4 h-4" />}
            Re-classify Types
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowClearConfirm(true)}
            disabled={clearing || total === 0}
            className="gap-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 dark:border-red-900"
            title="Delete all saved articles and start fresh"
          >
            {clearing
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <Trash2 className="w-4 h-4" />}
            Clear All
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowAdd(true)} className="gap-1.5">
            <Plus className="w-4 h-4" />
            Add Manually
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleViewDiscoveryHistory}
            className="gap-1.5"
            title="View past discovery runs"
          >
            <History className="w-4 h-4" />
            Discovery History
          </Button>
          <Button
            size="sm"
            onClick={() => setShowDiscover(true)}
            className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            <Sparkles className="w-4 h-4" />
            Find Backlinks
          </Button>
        </div>
      </motion.div>

      {/* ── Stats Row ──────────────────────────────────────────────────── */}
      {stats && (
        <motion.div {...fadeUp(0.05)} className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Total Pages',   value: stats.total,                 icon: Globe,        color: 'text-indigo-600 dark:text-indigo-400' },
            { label: 'Backlinks Verified', value: stats.backlinks_verified ?? 0, icon: ShieldCheck,  color: 'text-emerald-600 dark:text-emerald-400' },
            { label: 'Dofollow',      value: stats.dofollow_backlinks ?? 0, icon: Link2,       color: 'text-violet-600 dark:text-violet-400' },
            { label: 'Blogs',         value: stats.by_type?.blog || 0,    icon: BookOpen,     color: 'text-amber-600 dark:text-amber-400' },
          ].map(({ label, value, icon: Icon, color }) => (
            <Card key={label} className="gds-card">
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`p-2 rounded-lg bg-slate-100 dark:bg-slate-800 ${color}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-xl font-bold text-slate-800 dark:text-slate-100">{value}</p>
                  <p className="text-xs text-slate-500">{label}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </motion.div>
      )}

      {/* ── Split Panel ────────────────────────────────────────────────── */}
      <motion.div {...fadeUp(0.1)} className="grid lg:grid-cols-4 gap-6">

        {/* ── Left: Article List ──────────────────────────────────────── */}
        <Card className="gds-card lg:col-span-1 flex flex-col">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center justify-between">
              <span>Articles</span>
              {total > 0 && <Badge variant="secondary" className="text-xs">{total}</Badge>}
            </CardTitle>

            <div className="relative mt-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <Input
                className="pl-8 h-8 text-xs"
                placeholder="Search articles..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && fetchArticles(true)}
              />
            </div>

            <div className="flex flex-wrap gap-1 mt-2">
              {FILTER_TABS.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveType(tab.id)}
                  className={`px-2 py-0.5 rounded-full text-xs font-medium transition-all ${
                    activeType === tab.id
                      ? 'bg-indigo-600 text-white'
                      : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </CardHeader>

          <CardContent className="flex-1 overflow-y-auto max-h-[calc(100vh-22rem)] space-y-2 pr-1">
            {loading && articles.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 gap-2 text-slate-400">
                <Loader2 className="w-6 h-6 animate-spin" />
                <p className="text-xs">Loading articles...</p>
              </div>
            ) : articles.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-40 gap-3 text-center px-4">
                <Link2 className="w-10 h-10 text-slate-300 dark:text-slate-600" />
                <div>
                  <p className="text-sm font-medium text-slate-600 dark:text-slate-400">No articles yet</p>
                  <p className="text-xs text-slate-400 mt-1">
                    Click "Find Backlinks" to search for pages that link to your website.
                  </p>
                </div>
              </div>
            ) : (
              <>
                {articles.map(article => (
                  <ArticleListItem
                    key={article.article_id}
                    article={article}
                    active={selected?.article_id === article.article_id}
                    onSelect={setSelected}
                    onDelete={setDeleteTarget}
                  />
                ))}
                {hasMore && (
                  <button
                    onClick={() => { setPage(p => p + 1); fetchArticles(false); }}
                    className="w-full text-xs text-indigo-600 dark:text-indigo-400 hover:underline py-2"
                  >
                    Load more
                  </button>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* ── Right: Article Detail ───────────────────────────────────── */}
        <div className="lg:col-span-3">
          {!selected ? (
            <Card className="gds-card h-full flex items-center justify-center min-h-[400px]">
              <div className="text-center space-y-3 px-8">
                <div className="w-16 h-16 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto">
                  <ChevronRight className="w-8 h-8 text-slate-400" />
                </div>
                <p className="text-base font-medium text-slate-600 dark:text-slate-400">Select an article</p>
                <p className="text-sm text-slate-400">
                  {articles.length > 0
                    ? 'Click any article on the left to view its backlink details, screenshot, and content.'
                    : 'Find backlinks first to see them here.'}
                </p>
              </div>
            </Card>
          ) : (
            <Card className="gds-card">
              <CardContent className="p-6">
                <AnimatePresence mode="wait">
                  <ArticleDetailPanel
                    key={selected.article_id}
                    article={selected}
                    onRefresh={handleRefresh}
                    refreshing={refreshingId === selected.article_id}
                    targetDomain={targetDomain}
                  />
                </AnimatePresence>
              </CardContent>
            </Card>
          )}
        </div>
      </motion.div>

      {/* ── Dialogs ────────────────────────────────────────────────────── */}
      <DiscoverDialog
        open={showDiscover}
        onClose={() => setShowDiscover(false)}
        onSuccess={handleDiscoverSuccess}
        savedWebsiteUrl={targetWebsiteUrl || (targetDomain ? `https://${targetDomain}` : '')}
        targetSource={targetSource}
      />
      <AddArticleDialog
        open={showAdd}
        onClose={() => setShowAdd(false)}
        onSuccess={() => fetchArticles(true)}
        targetDomain={targetDomain}
      />
      <AlertDialog open={!!deleteTarget} onOpenChange={open => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Article</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteTarget?.title || deleteTarget?.url}"?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Discovery History Dialog ──────────────────────────────────── */}
      <Dialog open={discoveryRunsOpen} onOpenChange={setDiscoveryRunsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-indigo-500" /> Discovery History
            </DialogTitle>
          </DialogHeader>
          {discoveryRunsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
            </div>
          ) : discoveryRuns.length === 0 ? (
            <p className="text-sm text-slate-500 py-8 text-center">No discovery runs yet. Click "Find Backlinks" to start.</p>
          ) : (
            <div className="max-h-96 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800">
              {discoveryRuns.map((run) => (
                <div key={run.run_id} className="py-3 space-y-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-sm text-slate-800 dark:text-slate-200 truncate">
                      {run.inputs?.website_url}
                    </span>
                    <span className="text-xs text-slate-400 flex-shrink-0">
                      {new Date(run.created_at).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500">
                    <span className="text-indigo-600 font-medium">{run.outputs?.discovered || 0} checked</span>
                    <span>·</span>
                    <span className="text-slate-700 font-medium">{run.outputs?.saved || 0} saved</span>
                    <span>·</span>
                    <span className="text-emerald-600 font-medium">{run.outputs?.backlinks_verified || 0} backlinks</span>
                    {run.duration_ms && (
                      <>
                        <span>·</span>
                        <span>{(run.duration_ms / 1000).toFixed(1)}s</span>
                      </>
                    )}
                  </div>
                  {run.inputs?.industry && (
                    <p className="text-xs text-slate-400">Industry: {run.inputs.industry}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Clear All Confirmation ────────────────────────────────────── */}
      <AlertDialog open={showClearConfirm} onOpenChange={setShowClearConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear All Articles</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete all {total} saved off-page articles.
              Use this to reset discovery when the logic has been updated.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleClearAll} className="bg-red-600 hover:bg-red-700">
              {clearing ? 'Clearing…' : 'Clear All'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
